import type {
  DatumOffsetType,
  HorizontalDatumEntity,
  LateralDatumEntity,
  LegacyFoundationLateralDatumEntity,
  LegacyLevelDatumEntity,
  VerticalDatumEntity,
  DatumDirection,
  StackedLevelDatumEntity,
} from "building-types/datums/datumEntityTypes";
import {
  type AbsoluteVerticalDatumEntity,
  type DatumEntity,
  type LegacyFoundationVerticalDatumEntity,
  type LegacyWallVerticalDatumEntity,
  type WallHorizontalDatumEntity,
  type WallPlaneHorizontalDatumEntity,
} from "building-types/datums/datumEntityTypes";
import type {
  FoundationDatumVerticalAnchor,
  WallLateralDatumAnchor,
  WallVerticalDatumAnchor,
} from "building-types/datums/datumsTypesEnum";
import type { FoundationPathDatums } from "building-types/foundations/foundationPathTypes";
import type { Level, LevelAttributes } from "building-types/levels/levelTypes";
import type { WallLayerType } from "building-types/walls/wallEnums";
import type { WallSideGeneratedDatums } from "building-types/walls/wallSideGeneratedDatums";
import type { LevelInches, WorldInches } from "common/types/nominalNumbersTypes";
import type { DatumUsageType } from "./datumUsageTypes";

export interface ResolveVerticalDatumBaseProps {
  readonly extraOffset?: number;
}

export interface ResolveVerticalDatumBasePropsWithLevel extends ResolveVerticalDatumBaseProps {
  readonly level: Level;
}

export interface ResolveWallVerticalDatumProps extends ResolveVerticalDatumBasePropsWithLevel {
  readonly wallSide: { wallSideDatums: WallSideGeneratedDatums };
}

export interface ResolveFoundationVerticalDatumProps extends ResolveVerticalDatumBaseProps {
  readonly level: Level;
  readonly path: { datums?: FoundationPathDatums };
}

export interface ResolveLevelVerticalDatumProps extends ResolveVerticalDatumBasePropsWithLevel {
  readonly levelAttributes: LevelAttributes;
  readonly ignoreAnchor?: boolean;
}

export interface ResolveStackedLevelDatumBaseProps extends ResolveVerticalDatumBaseProps {
  readonly currentLevelId: string;
  readonly stackedAboveLevelId: string | undefined;
}

export interface ResolveAbsStackedLevelDatumProps extends ResolveStackedLevelDatumBaseProps {}

export interface ResolveRelStackedLevelDatumProps extends ResolveStackedLevelDatumBaseProps {
  readonly level: Level;
}

export interface ResolveComponentPlacementDatumProps extends ResolveVerticalDatumBaseProps {}

export interface ResolveRelComponentPlacementDatumProps extends ResolveVerticalDatumBasePropsWithLevel {}

export type ResolveAbsVerticalDatumProps =
  | ResolveWallVerticalDatumProps
  | ResolveFoundationVerticalDatumProps
  | ResolveLevelVerticalDatumProps
  | ResolveStackedLevelDatumBaseProps
  | ResolveComponentPlacementDatumProps;

export type ResolveRelVerticalDatumProps =
  | ResolveWallVerticalDatumProps
  | ResolveFoundationVerticalDatumProps
  | ResolveLevelVerticalDatumProps
  | ResolveRelStackedLevelDatumProps
  | ResolveRelComponentPlacementDatumProps;

export const datumUsageTypeToDatumAbstractionType = {
  WALL_VERTICAL: {} as WallVerticalDatumAbstraction,
  WALL_HORIZONTAL: {} as WallHorizontalDatumAbstraction,
  WALL_PLANE_HORIZONTAL: {} as WallPlaneHorizontalDatumAbstraction,
  FOUNDATION_VERTICAL: {} as FoundationVerticalDatumAbstraction,
  FOUNDATION_LATERAL: {} as FoundationLateralDatumAbstraction,
  LEVEL: {} as LevelDatumAbstraction,
  LEVEL_PLACEMENT: {} as LevelPlacementDatumAbstraction,
  COMPONENT_PLACEMENT: {} as AbsoluteVerticalDatumAbstractionBase,
  CEILING: {} as LevelDatumAbstraction,
} as const satisfies Record<
  DatumUsageType,
  | WallVerticalDatumAbstraction
  | WallHorizontalDatumAbstraction
  | WallPlaneHorizontalDatumAbstraction
  | FoundationVerticalDatumAbstraction
  | FoundationLateralDatumAbstraction
  | LevelDatumAbstraction
  | LevelPlacementDatumAbstraction
>;

export type VerticalDatumAnchor = WallVerticalDatumAnchor | FoundationDatumVerticalAnchor;

/**
 * DatumAbstraction interface
 */
export interface DatumAbstraction {
  getDatumEntity(): Readonly<DatumEntity>;
  getName(): string;
  getId(): string;
  getOffset(): number;
  getDirection(): DatumDirection;
  isValidForUsage<T extends DatumUsageType>(usageType: T): this is (typeof datumUsageTypeToDatumAbstractionType)[T];
}

/**
 * DatumAbstractionBase base class implementing some common methods
 */
export abstract class DatumAbstractionBase<T extends DatumEntity> implements DatumAbstraction {
  constructor(protected readonly datum: T) {}

  getDatumEntity(): Readonly<T> {
    return this.datum;
  }

  getName(): string {
    return this.datum.name;
  }

  getId(): string {
    return this.datum.id;
  }

  getOffset(): number {
    return this.datum.offset ?? 0;
  }

  getDirection(): DatumDirection {
    return this.datum.direction;
  }

  abstract isValidForUsage<T extends DatumUsageType>(
    usageType: T,
  ): this is (typeof datumUsageTypeToDatumAbstractionType)[T];
}

/**
 * Abstraction for any vertical datum
 */
export abstract class VerticalDatumAbstraction<
  T extends DatumEntity & VerticalDatumEntity<DatumOffsetType>,
> extends DatumAbstractionBase<T> {
  constructor(datum: T) {
    super(datum);
  }

  abstract isRoofDatum(): boolean;
  abstract isBaseVerticalDatumType(isBottomDatum: boolean, extraOffset?: number): boolean;
  abstract hasCompatibleAnchor(anchor: VerticalDatumAnchor): boolean;
  abstract resolveVerticalDatum(props: ResolveAbsVerticalDatumProps): WorldInches;
  abstract resolveVerticalDatumRelativeToLevel(props: ResolveRelVerticalDatumProps): LevelInches;
  abstract isValidForLevel(levelId: string | undefined): boolean;
}

/**
 * Abstraction for any horizontal datum
 */
export abstract class HorizontalDatumAbstraction<
  T extends DatumEntity & HorizontalDatumEntity<DatumOffsetType>,
> extends DatumAbstractionBase<T> {
  constructor(datum: T) {
    super(datum);
  }

  abstract resolveHorizontalDatum(datumRefLen: number, extraOffset?: number): number;
}

/**
 * Abstraction for any lateral datum
 */
export abstract class LateralDatumAbstraction<
  T extends DatumEntity & LateralDatumEntity<DatumOffsetType>,
> extends DatumAbstractionBase<T> {
  constructor(datum: T) {
    super(datum);
  }

  abstract resolveLateralDatum(): number;
  abstract resolveWallLayer(): WallLayerType | undefined;
  abstract hasCompatibleAnchor(anchor: WallLateralDatumAnchor): boolean;
}

/**
 * DatumAbstraction for absolute vertical datums
 */
export abstract class AbsoluteVerticalDatumAbstractionBase extends VerticalDatumAbstraction<AbsoluteVerticalDatumEntity> {
  constructor(datum: AbsoluteVerticalDatumEntity) {
    super(datum);
  }
}

/**
 * DatumAbstraction for legacy wall vertical and horizontal datums
 */
export abstract class LegacyWallVerticalDatumAbstractionBase extends VerticalDatumAbstraction<LegacyWallVerticalDatumEntity> {
  constructor(datum: LegacyWallVerticalDatumEntity) {
    super(datum);
  }
}

export abstract class LegacyWallHorizontalDatumAbstractionBase extends HorizontalDatumAbstraction<WallHorizontalDatumEntity> {
  constructor(datum: WallHorizontalDatumEntity) {
    super(datum);
  }
}

export abstract class WallPlaneHorizontalDatumAbstractionBase extends DatumAbstractionBase<WallPlaneHorizontalDatumEntity> {
  constructor(datum: WallPlaneHorizontalDatumEntity) {
    super(datum);
  }

  abstract resolveWallPlaneU(): number;
  abstract getPlaneHash(): string;
}

/**
 * DatumAbstraction for legacy foundation vertical and lateral datums
 */
export abstract class LegacyFoundationVerticalDatumAbstractionBase extends VerticalDatumAbstraction<LegacyFoundationVerticalDatumEntity> {
  constructor(datum: LegacyFoundationVerticalDatumEntity) {
    super(datum);
  }
}

export abstract class LegacyFoundationLateralDatumAbstractionBase extends LateralDatumAbstraction<LegacyFoundationLateralDatumEntity> {
  constructor(datum: LegacyFoundationLateralDatumEntity) {
    super(datum);
  }
}

/**
 * DatumAbstraction for legacy level vertical datums
 */
export abstract class LegacyLevelDatumAbstractionBase extends VerticalDatumAbstraction<LegacyLevelDatumEntity> {
  constructor(datum: LegacyLevelDatumEntity) {
    super(datum);
  }
}

/**
 * DatumAbstraction for stacked level datums
 */
export abstract class StackedLevelDatumAbstractionBase extends VerticalDatumAbstraction<StackedLevelDatumEntity> {
  constructor(datum: StackedLevelDatumEntity) {
    super(datum);
  }
}

export type WallVerticalDatumAbstraction =
  | LegacyWallVerticalDatumAbstractionBase
  | LegacyLevelDatumAbstractionBase
  | AbsoluteVerticalDatumAbstractionBase;

export type WallHorizontalDatumAbstraction = LegacyWallHorizontalDatumAbstractionBase;
export type WallPlaneHorizontalDatumAbstraction = WallPlaneHorizontalDatumAbstractionBase;

export type FoundationVerticalDatumAbstraction =
  | LegacyFoundationVerticalDatumAbstractionBase
  | AbsoluteVerticalDatumAbstractionBase;

export type FoundationLateralDatumAbstraction = LegacyFoundationLateralDatumAbstractionBase;
export type WallDatumAbstraction = WallVerticalDatumAbstraction | WallHorizontalDatumAbstraction;
export type FoundationDatumAbstraction = FoundationVerticalDatumAbstraction | FoundationLateralDatumAbstraction;
export type LevelDatumAbstraction = LegacyLevelDatumAbstractionBase | AbsoluteVerticalDatumAbstractionBase;
export type LevelPlacementDatumAbstraction = StackedLevelDatumAbstractionBase | AbsoluteVerticalDatumAbstractionBase;

export function isResolveWallVerticalDatumProps(
  props: ResolveAbsVerticalDatumProps | ResolveRelVerticalDatumProps,
): props is ResolveWallVerticalDatumProps {
  return "level" in props && "wallSide" in props;
}

export function isResolveFoundationVerticalDatumProps(
  props: ResolveAbsVerticalDatumProps | ResolveRelVerticalDatumProps,
): props is ResolveFoundationVerticalDatumProps {
  return "level" in props && "path" in props;
}

export function isResolveLevelVerticalDatumProps(
  props: ResolveAbsVerticalDatumProps | ResolveRelVerticalDatumProps,
): props is ResolveLevelVerticalDatumProps {
  return "level" in props && "levelAttributes" in props;
}

export function ensureDatumAbstractionUsageType<T extends DatumUsageType>(
  usageType: T,
  datumAbstraction: DatumAbstraction,
): (typeof datumUsageTypeToDatumAbstractionType)[T] {
  if (!datumAbstraction.isValidForUsage(usageType)) {
    throw new Error(`DatumAbstraction ${datumAbstraction.getId()} is not valid for usage type ${usageType}`);
  }

  return datumAbstraction;
}
